import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import TrustScoreBar from "@/components/TrustScoreBar";
import AdBlock from "@/components/AdBlock";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/Brand";
import { Plus, LogOut, Coins, Crown, Shirt, Settings, Sparkles, BookHeart, Flame, Gem, LayoutGrid, Radar } from "lucide-react";

export default function Sidebar({ characters, active, onSelect, onNewCharacter, onOpenWardrobe, onMemory, onStreak, onPricing, onPortal, onBlindDate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const Util = ({ testid, icon: Icon, label, onClick, disabled, tint = "text-brand" }) => (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors text-sm disabled:opacity-40">
      <Icon className={`w-4 h-4 ${tint}`} /> {label}
    </button>
  );

  return (
    <aside className="h-full flex flex-col bg-surface border-r border-white/10 min-h-0" data-testid="sidebar">
      {/* fixed top: brand + profile + tokens + trust (always visible) */}
      <div className="p-4 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-xl bg-brand flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white"><path d="M12 21s-7-4.5-9.5-9A5 5 0 0 1 12 6a5 5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>
          </div>
          <BrandMark className="text-base" />
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center font-heading font-semibold shrink-0">
            {(user?.name || "U")[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize flex items-center gap-1">
              {user?.tier === "premium" ? <><Crown className="w-3 h-3 text-brand" /> Premium</> : "Free tier"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <div data-testid="token-balance" className="flex-1 min-w-0 flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2">
            <Coins className="w-4 h-4 text-brand shrink-0" />
            <span className="text-sm font-semibold">{user?.tokens}</span>
            <span className="text-xs text-muted-foreground truncate">tokens</span>
          </div>
          {user?.tier !== "premium" && (
            <Button data-testid="sidebar-upgrade" onClick={onPricing}
              className="h-9 px-3 rounded-xl bg-brand hover:bg-brand-hover text-white text-xs transition-colors shrink-0">
              <Crown className="w-3.5 h-3.5 mr-1" /> Premium
            </Button>
          )}
        </div>
        {active && <div className="mt-4"><TrustScoreBar score={active.trust_score} /></div>}
      </div>

      {/* scrollable: companions + ad + utilities */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-4 space-y-2">
          <div className="flex items-center justify-between mb-1 px-1">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Companions</span>
            <button data-testid="new-character-button" onClick={onNewCharacter}
              className="w-7 h-7 rounded-lg bg-white/5 hover:bg-brand flex items-center justify-center transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {characters.map((c) => (
            <button key={c.id} data-testid={`character-item-${c.id}`} onClick={() => onSelect(c)}
              className={`w-full flex items-center gap-3 p-2.5 rounded-2xl transition-colors text-left ${active?.id === c.id ? "bg-brand/15 border border-brand/40" : "hover:bg-white/5 border border-transparent"}`}>
              <img src={c.active_outfit_url || c.avatar_url} alt={c.name} className="w-11 h-11 rounded-xl object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.age} • trust {c.trust_score}</p>
              </div>
            </button>
          ))}
          {characters.length === 0 && <p className="text-sm text-muted-foreground px-1 py-4">No companions yet.</p>}
        </div>

        <div className="px-2.5 flex justify-center"><AdBlock id="sidebar-ad" size="300x250" className="w-[300px] h-[250px] mb-4" /></div>

        <div className="p-4 border-t border-white/10 space-y-1 pb-6">
          <Util testid="wardrobe-toggle" icon={Shirt} label="Wardrobe Manager" onClick={onOpenWardrobe} disabled={!active} />
          <Util testid="memory-book-toggle" icon={BookHeart} label="Memory Book" onClick={onMemory} disabled={!active} />
          <Util testid="streak-toggle" icon={Flame} label="7-Day Streak" onClick={onStreak} />
          <Util testid="pricing-toggle" icon={Gem} label="Premium Plans" onClick={onPricing} />
          <Util testid="switch-blinddate" icon={Radar} label="Switch to Blind Date" onClick={onBlindDate} tint="text-cyan-300" />
          <Util testid="portal-toggle" icon={LayoutGrid} label="Back to Portal" onClick={onPortal} tint="text-muted-foreground" />
          <Util testid="new-companion-util" icon={Sparkles} label="Create Companion" onClick={onNewCharacter} tint="text-muted-foreground" />
          {user?.role === "admin" && <Util testid="go-admin" icon={Settings} label="Admin Portal" onClick={() => nav("/admin")} tint="text-muted-foreground" />}
          <Util testid="logout-button" icon={LogOut} label="Log out" onClick={() => { logout(); nav("/login"); }} tint="text-muted-foreground" />
        </div>
      </div>
    </aside>
  );
}
